import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/64bf5cca49ac62e2a4c4cc1d6d2bf6d5.jpeg)'
        }}
      />
      
      {/* Gradient Overlay - adjusted for better text contrast */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900/70 to-slate-950/85"></div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="inline-block mb-6 px-6 py-3 bg-gradient-to-r from-amber-500/20 to-amber-600/20 border-2 border-amber-500/50 rounded-full"
          >
            <span className="text-amber-400 font-bold text-sm sm:text-base flex items-center justify-center gap-2">
              <i className="ri-star-fill animate-pulse"></i>
              No Income Verification Required
              <i className="ri-star-fill animate-pulse"></i>
            </span>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
          >
            DSCR Loans for<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
              Real Estate Investors
            </span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="text-xl sm:text-2xl md:text-3xl text-blue-100 mb-8 max-w-4xl mx-auto leading-relaxed"
          >
            Finance unlimited investment properties without tax returns or W2s. 
            Qualify based on property cash flow, not personal income.
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12"
          >
            <Link
              to="/apply"
              className="group relative px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 rounded-xl font-bold text-lg hover:from-amber-600 hover:to-amber-700 transition-all shadow-2xl hover:shadow-amber-500/50 hover:scale-105 whitespace-nowrap overflow-hidden"
            >
              <span className="relative z-10 flex items-center">
                <i className="ri-file-text-line mr-2"></i>
                Get Pre-Qualified Now
                <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-amber-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </Link>
            
            <a
              href="#calculator"
              className="group px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-xl font-bold text-lg hover:bg-white/20 hover:border-white/50 transition-all shadow-xl whitespace-nowrap"
            >
              <i className="ri-calculator-line mr-2"></i>
              Calculate Your DSCR
              <i className="ri-arrow-down-line ml-2 group-hover:translate-y-1 transition-transform"></i>
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div 
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto"
          >
            <motion.div 
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { 
                  opacity: 1, 
                  y: 0,
                  transition: { 
                    duration: 0.5,
                    delay: 0.4,
                    ease: "easeOut"
                  }
                }
              }}
              whileHover={{ scale: 1.05, y: -5 }}
              className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:border-amber-500/50 transition-all will-change-transform"
            >
              <div className="text-4xl font-bold text-amber-400 mb-2">6.5%</div>
              <div className="text-blue-100 font-semibold">Starting Rate</div>
            </motion.div>
            
            <motion.div 
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { 
                  opacity: 1, 
                  y: 0,
                  transition: { 
                    duration: 0.5,
                    delay: 0.5,
                    ease: "easeOut"
                  }
                }
              }}
              whileHover={{ scale: 1.05, y: -5 }}
              className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:border-amber-500/50 transition-all will-change-transform"
            >
              <div className="text-4xl font-bold text-amber-400 mb-2">21 Days</div>
              <div className="text-blue-100 font-semibold">Average Close Time</div>
            </motion.div>
            
            <motion.div 
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { 
                  opacity: 1, 
                  y: 0,
                  transition: { 
                    duration: 0.5,
                    delay: 0.6,
                    ease: "easeOut"
                  }
                }
              }}
              whileHover={{ scale: 1.05, y: -5 }}
              className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:border-amber-500/50 transition-all will-change-transform"
            >
              <div className="text-4xl font-bold text-amber-400 mb-2">∞</div>
              <div className="text-blue-100 font-semibold">Property Limit</div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="text-white/60 hover:text-white transition-colors cursor-pointer"
        >
          <i className="ri-arrow-down-double-line text-3xl"></i>
        </motion.div>
      </motion.div>
    </section>
  );
}
